import { EventMetadata, EventTicketsBlock } from '@pages/event/api/useFetchEvent';
import { DEFAULT_TYPE_STATISTIC, EVENT_STATISTICS, EventStatistic, STATISTIC_TYPE } from '@entities/event';
import { shouldShowTicketPresenceStatus } from '@features/event-participant-card/lib/should-show-ticket-presence-status';
import { EventStatisticsHeader } from './EventStatisticsHeader';
import { useMemo, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface EventStatisticsProps {
  ticketsMetadata?: EventTicketsBlock;
  metadata?: EventMetadata | null;
  eventDateStart?: string | null;
}

interface StatisticData {
  total: number;
  countAdultGuests: number;
  countAdultWithChildGuests: number;
  countChildGuests: number;
  checkedCount: number;
  countAcceptedInvites: number;
  countDeclinedInvites: number;
  totalInvites: number;
  ticketsSent: number;
  falsedCount: number;
}

interface StatisticItem {
  value: string;
  label: string;
  displayValue: number;
  widthRatio: number;
}

const formatNumber = (number: number) => new Intl.NumberFormat('ru-RU').format(number);

const shouldShowStatisticField = (
  typeId: number,
  valueKey: string,
  displayValue: number,
  data: StatisticData
): boolean => {
  switch (typeId) {
    case STATISTIC_TYPE.LEAD_COLLECTOR:
      if (valueKey === 'total') {
        return true;
      }
      return displayValue > 0;

    case STATISTIC_TYPE.INVITATIONS: {
      const hasInviteResponses = data.countAcceptedInvites > 0 || data.countDeclinedInvites > 0;
      if (!hasInviteResponses) {
        return valueKey === 'totalInvites';
      }
      return true;
    }

    case STATISTIC_TYPE.TICKETS:
      return true;

    case STATISTIC_TYPE.GENERAL: {
      if (data.countAcceptedInvites > 0) {
        return true;
      }
      return valueKey === 'total' || valueKey === 'checkedCount' || valueKey === 'falsedCount';
    }

    default:
      return displayValue > 0;
  }
};

const buildStatisticItems = (typeStatistic: EventStatistic, data: StatisticData): StatisticItem[] => {
  const items: StatisticItem[] = [];

  typeStatistic.values.forEach((valueKey, index) => {
    const displayValue = data[valueKey as keyof StatisticData] ?? 0;

    if (!shouldShowStatisticField(typeStatistic.id, valueKey, displayValue, data)) {
      return;
    }

    items.push({
      value: valueKey,
      label: typeStatistic.labels[index],
      displayValue,
      widthRatio: 0,
    });
  });

  if (!items.length) {
    return items;
  }

  const baseValue = items[0].displayValue || 1;

  return items.map((item) => ({
    ...item,
    widthRatio: Math.min(1, item.displayValue / baseValue),
  }));
};

export const EventStatistics = ({ ticketsMetadata, metadata, eventDateStart }: EventStatisticsProps) => {
  const [typeStatistic, setTypeStatistic] = useState(EVENT_STATISTICS[DEFAULT_TYPE_STATISTIC]);

  const statisticData = useMemo<StatisticData>(() => {
    const total = ticketsMetadata?.total ?? 0;
    const isEventStarted = shouldShowTicketPresenceStatus(eventDateStart);
    const checkedCount = isEventStarted ? ticketsMetadata?.checkedCount ?? 0 : 0;

    return {
      total,
      countAdultGuests: ticketsMetadata?.countAdultGuests ?? 0,
      countAdultWithChildGuests: ticketsMetadata?.countAdultWithChildGuests ?? 0,
      countChildGuests: ticketsMetadata?.countChildGuests ?? 0,
      checkedCount,
      countAcceptedInvites: metadata?.countAcceptedInvites ?? 0,
      countDeclinedInvites: metadata?.countDeclinedInvites ?? 0,
      totalInvites: metadata?.totalInvites ?? 0,
      ticketsSent: metadata?.ticketsSent ?? 0,
      falsedCount: isEventStarted ? total - checkedCount : 0,
    };
  }, [metadata, ticketsMetadata, eventDateStart]);

  const statisticItems = useMemo(
    () => buildStatisticItems(typeStatistic, statisticData),
    [typeStatistic, statisticData]
  );

  return (
    <div>
      <EventStatisticsHeader typeStatistic={typeStatistic} setTypeStatistic={setTypeStatistic} />

      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.statisticText)}>
          {statisticItems.map((item) => (
            <span key={item.value}>{item.label}</span>
          ))}
        </div>

        <div {...stylex.props(styles.statistic)}>
          {statisticItems.map((item) => (
            <span key={item.value} {...stylex.props(styles.calculateWidth(item.widthRatio))}>
              {formatNumber(item.displayValue)}
            </span>
          ))}
        </div>
      </div>

      <span {...stylex.props(styles.description)}>{typeStatistic.description}</span>
    </div>
  );
};
